import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import storageSession from "redux-persist/es/storage/session";
import {
  getPreviousNPeriods,
  getSelectedPerformanceYearData,
  isValidPeriodFormat,
  isValidPYFormat,
  getCurrentPeriod,
  calculatePerformanceYear,
  getPeriodFromPY,
  getPYFromPeriod,
} from "../common/util/period";
import { Periods } from "../services/MyBizShell/mybizShellAPI.types";
import { setShellConfig } from "./boot";
import { setNavigatedByBusiness } from "./businessSelector";

export type Period = {
  selectedPeriod: string;
  selectedPerformanceYear: string;
  monthPeriods: Array<string>;
  annualPeriods: Array<string>;
  periods: Array<string>;
  formattedPeriods: Array<{ period: string; display: string }>;
  selectorType: SELECTOR_TYPE;
  isNextPeriod: boolean;
  navigatedByPeriodChange: boolean;
  customPeriods: Array<string>;
};

export enum SELECTOR_TYPE {
  PYMONTHS = "PYMONTHS",
  MONTHLY = "MONTHLY",
  PY = "PY",
  CONFIGURABLE = "CONFIGURABLE",
}

const initialPeriod = getCurrentPeriod();
const initialPerformanceYear = calculatePerformanceYear(initialPeriod);
const initialPY = `${initialPerformanceYear}08`;

const initialState: Period = {
  selectedPeriod: initialPeriod,
  selectedPerformanceYear: initialPerformanceYear,
  monthPeriods: [initialPeriod],
  annualPeriods: [initialPY],
  periods: [initialPeriod],
  formattedPeriods: [],
  selectorType: SELECTOR_TYPE.MONTHLY,
  isNextPeriod: false,
  navigatedByPeriodChange: false,
  customPeriods: [],
};

const getFilteredPeriod = (state: Period) => {
  const { selectedPYEndPeriod } = getSelectedPerformanceYearData(state.selectedPeriod);
  const listOfCurrentPYPeriod = getPreviousNPeriods(selectedPYEndPeriod, 12, true);
  const filteredPeriod = listOfCurrentPYPeriod.filter((p) => p <= state.selectedPeriod || p <= getCurrentPeriod());
  return filteredPeriod;
};

// Determines the period selector type by passing in action.payload and current state.
// Returns an object with "periods" and "selectedPeriod" accordingly.
// To enhance modularity, the 'dates' parameter is optional.
const getPeriods = (state: Period, selectorType: SELECTOR_TYPE, dates?: Periods | null) => {
  switch (selectorType) {
    case SELECTOR_TYPE.CONFIGURABLE:
      return getCustomPeriods(state);
    case SELECTOR_TYPE.PY:
      return getPYPeriods(state, dates);
    case SELECTOR_TYPE.PYMONTHS:
      return getPYMonthsPeriods(state);
    case SELECTOR_TYPE.MONTHLY: // Fallthrough
    default:
      return getMonthlyPeriods(state, dates);
  }
};

// Helper function for setting the selected period within PY.
const setPeriodWithinPY = (period: string, annualPeriods: string[]) => {
  const lastMonth = getSelectedPerformanceYearData(period).selectedPYEndPeriod;
  const currentPeriod = getCurrentPeriod();
  const withIn = annualPeriods.includes(period);
  if (currentPeriod === period && withIn) {
    return period;
  } else {
    if (currentPeriod <= lastMonth) {
      return currentPeriod;
    }
    return lastMonth;
  }
};

// Helper function for SELECTOR_TYPE.PY
const getPYPeriods = (state: Period, dates?: Periods | null) => {
  return {
    periods: dates ? dates.performanceYears.map((p) => p.period) : state.annualPeriods,
    selectedPeriod: state.annualPeriods[0],
  };
};

// Helper function for SELECTOR_TYPE.PYMONTHS
const getPYMonthsPeriods = (state: Period) => {
  return {
    periods: getFilteredPeriod(state),
    selectedPeriod: state.selectedPeriod,
  };
};

// Helper function for SELECTOR_TYPE.MONTHLY
const getMonthlyPeriods = (state: Period, dates?: Periods | null) => {
  return {
    periods: dates ? dates.monthlyPeriods : state.monthPeriods,
    selectedPeriod: state.monthPeriods[0],
  };
};

const getCustomPeriods = (state: Period) => {
  return {
    periods: state.customPeriods,
    selectedPeriod: state.customPeriods[0],
  };
};

const calculateNumberOfPeriods = (dates: Periods, state: Period) => {
  const { periods } = getPeriods(state, state.selectorType, dates);

  return {
    monthPeriods: dates.monthlyPeriods,
    annualPeriods: dates.performanceYears.map((period) => period.period),
    periods: periods,
    formattedPeriods: dates.performanceYears,
  };
};

const { actions, reducer } = createSlice({
  name: "period",
  initialState,
  reducers: {
    /**
     * updates the selected period
     * @param action `{period: string}`
     */
    updatePeriod: (state, action: PayloadAction<{ period: string }>) => {
      // Validate period format and fallback if invalid
      const validatedPeriod = isValidPeriodFormat(action.payload.period) ? action.payload.period : getCurrentPeriod();

      const newPerformanceYear = getPYFromPeriod(validatedPeriod);
      return {
        ...state,
        selectedPeriod: validatedPeriod,
        selectedPerformanceYear: newPerformanceYear,
        navigatedByPeriodChange: validatedPeriod !== state.selectedPeriod,
      };
    },
    /**
     * updates the selected performance year and corresponding period
     * @param action `{performanceYear: string}`
     */
    updatePerformanceYear: (state, action: PayloadAction<{ performanceYear: string }>) => {
      // Validate PY format and fallback if invalid
      const validatedPY = isValidPYFormat(action.payload.performanceYear)
        ? action.payload.performanceYear
        : calculatePerformanceYear(getCurrentPeriod());

      // Always use current period to determine if selected PY is current PY
      const currentPeriod = getCurrentPeriod();
      const newPeriod = getPeriodFromPY(validatedPY, currentPeriod);
      return {
        ...state,
        selectedPerformanceYear: validatedPY,
        selectedPeriod: newPeriod,
        navigatedByPeriodChange: true,
      };
    },
    /**
     * change period selector type (PY or MONTHLY) with automatic synchronization
     * @param `SELECTOR_TYPE`
     */
    updateSelectorType: (state, action: PayloadAction<SELECTOR_TYPE>) => {
      // only update selector if the type has changed
      if (state.selectorType !== action.payload) {
        const { periods } = getPeriods(state, action.payload);

        // Automatic synchronization logic
        let newSelectedPeriod = state.selectedPeriod;
        let newSelectedPerformanceYear = state.selectedPerformanceYear;

        // When switching to MONTHLY or PYMONTHS from PY
        if (
          (action.payload === SELECTOR_TYPE.MONTHLY || action.payload === SELECTOR_TYPE.PYMONTHS) &&
          state.selectorType === SELECTOR_TYPE.PY
        ) {
          // Determine appropriate period when switching to MONTHLY selector
          // Use current period if PY matches current PY, otherwise use last month of PY
          const currentPeriod = getCurrentPeriod();
          const currentPY = getPYFromPeriod(currentPeriod);

          // If selected PY matches current PY, use current period
          if (state.selectedPerformanceYear === currentPY) {
            newSelectedPeriod = currentPeriod;
          } else {
            // Otherwise, use last month of selected PY (August)
            newSelectedPeriod = `${state.selectedPerformanceYear}08`;
          }

          // Ensure PY is maintained
          newSelectedPerformanceYear = state.selectedPerformanceYear;
        }

        // When switching to PY from MONTHLY or PYMONTHS
        else if (
          action.payload === SELECTOR_TYPE.PY &&
          (state.selectorType === SELECTOR_TYPE.MONTHLY || state.selectorType === SELECTOR_TYPE.PYMONTHS)
        ) {
          // Convert current period to corresponding PY
          const calculatedPY = getPYFromPeriod(state.selectedPeriod);
          const currentPeriod = getCurrentPeriod();
          const currentPY = getPYFromPeriod(currentPeriod);

          // Set the PY based on the selected period
          newSelectedPerformanceYear = calculatedPY;

          // Determine the appropriate period for the PY selector
          // If the calculated PY matches current PY, use current period
          // Otherwise, use the last month of the selected PY (August)
          if (calculatedPY === currentPY) {
            newSelectedPeriod = currentPeriod;
          } else {
            // For historical PYs, use the last month (August)
            newSelectedPeriod = `${calculatedPY}08`;
          }
        }

        // For CONFIGURABLE type, maintain current values but ensure consistency
        else if (action.payload === SELECTOR_TYPE.CONFIGURABLE) {
          // Ensure state consistency between selectedPeriod and selectedPerformanceYear
          newSelectedPerformanceYear = getPYFromPeriod(state.selectedPeriod);
        }

        // For any other transitions, ensure state consistency
        else {
          // Always maintain consistency between period and PY
          if (newSelectedPeriod !== state.selectedPeriod) {
            newSelectedPerformanceYear = getPYFromPeriod(newSelectedPeriod);
          } else if (newSelectedPerformanceYear !== state.selectedPerformanceYear) {
            newSelectedPeriod = getPeriodFromPY(newSelectedPerformanceYear, getCurrentPeriod());
          }
        }

        return {
          ...state,
          selectorType: action.payload,
          periods: periods,
          selectedPeriod: newSelectedPeriod,
          selectedPerformanceYear: newSelectedPerformanceYear,
          isNextPeriod: true,
        };
      }
      return state;
    },
    setCustomPeriodsOverride: (state, action: PayloadAction<{ periods: string[] }>) => {
      return { ...state, customPeriods: action.payload.periods };
    },
    /**
     * updates next period flag based on the next arrow period selector click
     * @param action `Boolean`
     */
    updateNextPeriodFlag(state, action: PayloadAction<{ isNextPeriod: boolean }>) {
      return { ...state, isNextPeriod: action.payload.isNextPeriod };
    },
    setNavigatedByPeriod(state, action) {
      return {
        ...state,
        navigatedByPeriodChange: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setShellConfig, (state, { payload }) => {
      return {
        ...state,
        ...calculateNumberOfPeriods(payload.periods, state),
      };
    });
    builder.addCase(setNavigatedByBusiness, (state, { payload }) => {
      if (payload) {
        const currentPeriod = getCurrentPeriod();
        return {
          ...state,
          selectedPeriod: currentPeriod,
          selectedPerformanceYear: calculatePerformanceYear(currentPeriod),
        };
      }
      return state;
    });
  },
});

export const {
  updatePeriod,
  updatePerformanceYear,
  updateSelectorType,
  updateNextPeriodFlag,
  setNavigatedByPeriod,
  setCustomPeriodsOverride,
} = actions;

// Export raw reducer for testing
export const periodReducer = reducer;

const periodPersistConfig = {
  key: "period",
  storage: storageSession,
  stateReconciler: autoMergeLevel2,
};

export default persistReducer<Period>(periodPersistConfig, reducer);
